import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { jsPDF } from "jspdf";
import { useThemeMode } from "../../ThemeContext";
import { usePermissions } from "../../contexts/PermissionContext";
import { dataAPI } from "../../helpers/api_service";
import { APP_URLS } from "../../helpers/url_helper";

function CompanyFormList() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  const { hasReadPermission } = usePermissions();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [searchTitle, setSearchTitle] = useState("");
  const showFormRecordsColumn = hasReadPermission("companyforms");

  useEffect(() => {
    let mounted = true;

    const loadTemplates = async () => {
      try {
        setLoading(true);
        setErrorMessage("");
        const response = await dataAPI.getDigitalFormClientForms();
        if (!mounted) return;
        setTemplates(Array.isArray(response?.data) ? response.data : []);
      } catch (error) {
        if (!mounted) return;
        setErrorMessage(error?.message || "Failed to load company forms.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTemplates();
    return () => {
      mounted = false;
    };
  }, []);

  const emptyText = useMemo(() => {
    if (errorMessage) return errorMessage;
    return "No company forms available.";
  }, [errorMessage]);

  const activeTemplates = templates.filter((item) => item?.is_active !== false);
  const filteredTemplates = activeTemplates.filter((item) =>
    String(item?.title || "")
      .toLowerCase()
      .includes(searchTitle.trim().toLowerCase()),
  );

  const safeFileName = (value) =>
    String(value || "company-form")
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || "company-form";

  const getTemplateSections = (template) => {
    const source = Array.isArray(template?.schema_json) ? template.schema_json : [];
    if (!source.length) return [];
    const isSectionSchema = Array.isArray(source[0]?.fields);
    if (isSectionSchema) {
      return source.map((section, sectionIdx) => ({
        id: section?.id || `section-${sectionIdx + 1}`,
        title: section?.title || `Section ${sectionIdx + 1}`,
        description: section?.description || "",
        fields: Array.isArray(section?.fields) ? section.fields : [],
      }));
    }
    return [
      {
        id: "section-1",
        title: "Section 1",
        description: "",
        fields: source,
      },
    ];
  };

  const downloadTemplatePdf = (template) => {
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 40;
    const contentWidth = pageWidth - margin * 2;
    let y = margin;

    const ensurePageSpace = (required = 24) => {
      if (y + required > pageHeight - margin) {
        doc.addPage();
        y = margin;
      }
    };

    const addWrappedText = (text, fontSize = 11, gap = 8) => {
      doc.setFontSize(fontSize);
      const lines = doc.splitTextToSize(String(text || ""), contentWidth);
      const lineHeight = fontSize + 4;
      ensurePageSpace(lines.length * lineHeight + gap);
      doc.text(lines, margin, y);
      y += lines.length * lineHeight + gap;
    };

    const drawLine = (lineY, fromX = margin, toX = pageWidth - margin) => {
      ensurePageSpace(20);
      doc.line(fromX, lineY, toX, lineY);
    };

    doc.setFont("helvetica", "bold");
    addWrappedText(template?.title || "Company Form", 18, 2);

    doc.setFont("helvetica", "normal");
    addWrappedText(template?.description || "No description", 11, 20);

    const sections = getTemplateSections(template);
    let questionNumber = 1;

    sections.forEach((section, sectionIndex) => {
      doc.setFont("helvetica", "bold");
      addWrappedText(section?.title || `Section ${sectionIndex + 1}`, 14, 4);
      doc.setFont("helvetica", "normal");
      if (section?.description) {
        addWrappedText(section.description, 10, 8);
      }

      (section?.fields || []).forEach((field) => {
        const fieldLabel = field?.label || "Untitled Field";
        const fieldType = String(field?.type || "text").toLowerCase();

        doc.setFont("helvetica", "bold");
        addWrappedText(`${questionNumber}. ${fieldLabel}`, 12, 8);
        doc.setFont("helvetica", "normal");

        if (
          fieldType === "radio" ||
          fieldType === "checkbox" ||
          fieldType === "dropdown" ||
          fieldType === "multi_select"
        ) {
          const options = Array.isArray(field?.options) ? field.options : [];
          const hasDataSource = String(field?.dataSource || "").trim().length > 0;
          if (hasDataSource) {
            addWrappedText(
              `(Options loaded from data source: ${field.dataSource})`,
              10,
              6,
            );
          } else {
            const printableOptions = options.length ? options : ["Option 1", "Option 2"];
            printableOptions.forEach((option) => {
              addWrappedText(`[   ] ${option}`, 10, 4);
            });
          }
          y += 14;
          questionNumber += 1;
          return;
        }

        if (fieldType === "attachment") {
          addWrappedText("Attachment upload field", 10, 6);
          drawLine(y + 14);
          y += 28;
          questionNumber += 1;
          return;
        }

        if (fieldType === "textarea") {
          ensurePageSpace(110);
          drawLine(y + 14);
          drawLine(y + 36);
          drawLine(y + 58);
          drawLine(y + 80);
          y += 92;
          questionNumber += 1;
          return;
        }

        ensurePageSpace(44);
        drawLine(y + 14);
        y += 38;
        questionNumber += 1;
      });

      y += 6;
    });

    doc.save(`${safeFileName(template?.title)}.pdf`);
  };

  const shareTemplate = async (template) => {
    const shareTitle = template?.title || "Company Form";
    const shareText = `Please fill this form: ${shareTitle}`;
    const shareUrl = `${window.location.origin}${APP_URLS.COMPANY_FORM_FILL}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        });
        return;
      }

      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(`${shareText} - ${shareUrl}`);
        alert("Form link copied to clipboard.");
        return;
      }

      alert(`${shareText} - ${shareUrl}`);
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={containerStyle(mode)}>
        <div style={{ marginBottom: 14 }}>
          <input
            type="text"
            value={searchTitle}
            onChange={(event) => setSearchTitle(event.target.value)}
            placeholder="Search by form title..."
            style={searchInputStyle(mode)}
          />
        </div>

        {!loading && !filteredTemplates.length ? (
          <div style={emptyCardStyle(mode)}>{emptyText}</div>
        ) : (
          <div style={tableWrapperStyle(mode)}>
            <table style={tableStyle()}>
              <colgroup>
                <col style={{ width: "18%" }} />
                <col style={{ width: "34%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: showFormRecordsColumn ? "24%" : "38%" }} />
                {showFormRecordsColumn && <col style={{ width: "14%" }} />}
              </colgroup>
              <thead>
                <tr>
                  <th style={headCellStyle(mode)}>Title</th>
                  <th style={headCellStyle(mode)}>Description</th>
                  <th style={headCellStyle(mode)}>Fields</th>
                  <th style={headCellStyle(mode)}>Action</th>
                  {showFormRecordsColumn && (
                    <th style={{ ...headCellStyle(mode), textAlign: "center" }}>Form records</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((template) => (
                  <tr key={template.id} style={rowStyle(mode)}>
                    <td style={bodyCellStyle(mode)}>
                      {template?.title || "Untitled Form"}
                    </td>
                    <td style={descriptionCellStyle(mode)}>
                      {template?.description || "No description"}
                    </td>
                    <td style={bodyCellStyle(mode)}>
                      {getTemplateSections(template).reduce(
                        (total, section) =>
                          total + (Array.isArray(section?.fields) ? section.fields.length : 0),
                        0,
                      )}
                    </td>
                    <td style={bodyCellStyle(mode)}>
                      <div style={actionWrapStyle()}>
                        <button
                          type="button"
                          style={openBtnStyle()}
                          onClick={() =>
                            navigate(APP_URLS.COMPANY_FORM_FILL, {
                              state: { template },
                            })
                          }
                        >
                          Open
                        </button>
                        <button
                          type="button"
                          style={downloadBtnStyle()}
                          onClick={() => downloadTemplatePdf(template)}
                        >
                          Download PDF
                        </button>
                        <button
                          type="button"
                          style={shareBtnStyle()}
                          onClick={() => shareTemplate(template)}
                        >
                          Share
                        </button>
                      </div>
                    </td>
                    {showFormRecordsColumn && (
                      <td style={{ ...bodyCellStyle(mode), textAlign: "center" }}>
                        <button
                          type="button"
                          style={recordsBtnStyle()}
                          onClick={() =>
                            navigate(APP_URLS.COMPANY_FORM_RECORDS, {
                              state: { form: template },
                            })
                          }
                        >
                          View Records
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const containerStyle = (mode) => ({
  border: "none",
  borderRadius: 12,
  background: mode === "dark" ? "#121212" : "#fff",
  padding: 16,
  width: "100%",
});

const searchInputStyle = (mode) => ({
  width: "100%",
  maxWidth: 360,
  border: `1px solid ${mode === "dark" ? "#4a4a4a" : "#d0d5dd"}`,
  borderRadius: 8,
  background: mode === "dark" ? "#191919" : "#fff",
  color: mode === "dark" ? "#fff" : "#111",
  padding: "9px 10px",
  fontSize: 13,
});

const emptyCardStyle = (mode) => ({
  border: `1px dashed ${mode === "dark" ? "#475467" : "#cbd5e1"}`,
  borderRadius: 10,
  padding: 14,
  color: mode === "dark" ? "#cbd5e1" : "#475569",
  fontSize: 14,
});

const tableWrapperStyle = (mode) => ({
  border: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  borderRadius: 10,
  overflowX: "auto",
  width: "100%",
});

const tableStyle = () => ({
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "fixed",
});

const headCellStyle = (mode) => ({
  textAlign: "left",
  padding: "11px 12px",
  borderBottom: `1px solid ${mode === "dark" ? "#344054" : "#e2e8f0"}`,
  background: mode === "dark" ? "#171717" : "#f8fafc",
  color: mode === "dark" ? "#e4e7ec" : "#334155",
  fontSize: 13,
  fontWeight: 600,
});

const rowStyle = (mode) => ({
  borderBottom: `1px solid ${mode === "dark" ? "#2f2f2f" : "#f1f5f9"}`,
});

const bodyCellStyle = (mode) => ({
  padding: "11px 12px",
  color: mode === "dark" ? "#d0d5dd" : "#0f172a",
  fontSize: 13,
});

const descriptionCellStyle = (mode) => ({
  ...bodyCellStyle(mode),
  maxWidth: 420,
  whiteSpace: "normal",
  wordBreak: "break-word",
  lineHeight: 1.45,
});

const openBtnStyle = () => ({
  border: "none",
  background: "#009391",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const downloadBtnStyle = () => ({
  border: "none",
  background: "#2563eb",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const shareBtnStyle = () => ({
  border: "none",
  background: "#7c3aed",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const recordsBtnStyle = () => ({
  border: "none",
  background: "#0ea5a4",
  color: "#fff",
  borderRadius: 8,
  fontSize: 12,
  fontWeight: 600,
  padding: "7px 12px",
  cursor: "pointer",
});

const actionWrapStyle = () => ({
  display: "inline-flex",
  gap: 8,
  alignItems: "center",
});

export default CompanyFormList;
